import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ConfirmDialogService, FileDragNDropComponent, TaggingOptions, UiKitModule } from '@serviceplan/ui-kit';
import {
  AdvertisingMaterialOptions,
  AssetListFileObject,
  AssetUIModel,
  AssetsGenerateUrlService,
  CanPipe,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  FieldType,
  FileObject,
  LufthansaGroupID,
  StepperGroupModel,
  StepperModel,
  TableColumn,
  ValueListsService,
  anyAsset,
  createAssetFileKey,
} from '@thehubz/client/core';
import {
  Asset,
  AssetsApiService,
  Collection,
  CollectionsApiService,
  CompanyBrand,
  CreateAssetRelationDto,
  CreateCollectionDto,
  NotifyOptions,
  ProjectsApiService,
  RelationsApiService,
  TasksApiService,
  UpdateAssetDto,
  UpdateAssetManyDto,
  UserInfo,
  UsersTaggingApiService,
} from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, AssetMetadata, AssetTypeEnum } from '@thehubz/shared/model';
import { Subscription } from 'rxjs';
import { AssetCommonService } from '../../services/asset-common.service';
import { AssetUploadService } from '../../services/asset-upload.service';
import { CollectionStepperDialogService } from '../../services/collection-stepper-dialog.service';
import { ProductBrandSelectionService } from '../../services/product-brand-selection.service';
import { BuyoutRightsStandardComponent } from '../asset-buyout-rights/buyout-rights-standard/buyout-rights-standard.component';
import { AssetRelationsUIModel } from '../asset-detail-view/asset-relations-form-control/asset-relations-form-control.component';
import { BuyoutRightsColumnData } from '../asset-detail-view/asset-usage-rights/table-column-data';
import { AssetItemBasicComponent } from '../asset-item-basic/asset-item-basic.component';
import { AssetSettingsStepperComponent } from '../asset-settings-stepper/asset-settings-stepper.component';
import {
  checkEndDateAgainstStartDate,
  checkStartDateAgainstEndDate,
  constructFormValues,
  handleArvalBranding,
  handleLufthansaBranding,
  handleSeatBranding,
  handleStandardBranding,
  handleVKBBranding,
  handleVorwerkBranding,
  processNonStandardBuyoutRights,
  processStandardBuyoutRights,
  updateAgencyOptions,
} from './asset-upload.util';
import { AssetSettingsStepperConfigService } from './stepper-config/asset-settings-stepper-config.service';
import { BuyoutRightsConfig, getBuyoutRightsConfig } from './stepper-config/asset-settings-stepper.constants';

@Component({
  selector: 'thehubz-asset-upload',
  templateUrl: './asset-upload.component.html',
  styleUrls: ['./asset-upload.component.scss'],
  providers: [CollectionStepperDialogService, ProductBrandSelectionService],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatProgressBarModule,
    UiKitModule,
    AssetItemBasicComponent,
    AssetSettingsStepperComponent,
    BuyoutRightsStandardComponent,
  ],
})
export class AssetUploadComponent implements OnInit, OnDestroy, AfterViewChecked {
  files: FileObject[] = [];
  assetListFiles: AssetListFileObject[] = [];
  assetListFilesDraft: Asset[] = [];
  existingAssetListFiles: AssetUIModel[] = [];
  selectedAssetIds: string[] = [];

  clients: AnyType[] = [];
  brands: CompanyBrand[] = [];
  assets: Asset[] = [];
  assetsMap = new Map<string, Asset>(); // used for asset relations
  attachments: AnyType[] = [];
  form!: UntypedFormGroup;
  assetUploadBuyoutRights!: UntypedFormArray;
  progress = 0;
  fileLength = 0;
  currentUploadFileName = '';
  assetExistingFlag = false;
  assetDuplicateFlag = false;
  uploadInProgress = false;
  uploadStartSubscription!: Subscription;
  uploadCompleteSubscription!: Subscription;
  progressSubscription!: Subscription;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  tags: string[] = [];
  mediumOptions = AdvertisingMaterialOptions;
  expiryRequired = true;
  errorFlag = true;
  draftFlag = false;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
  };
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text
  AnyAsset = anyAsset;
  users!: { id: string; name: string; email: string }[];
  stepperData!: {
    stepperData: StepperModel;
    additionalData: AnyType[];
    callFrom: string;
    editable: boolean;
  };
  currentStep!: StepperGroupModel;
  hasFilesError = false;
  isReady = false;
  brandingKey!: CustomClients;
  CustomClients = CustomClients;
  buyoutRightsConfigs!: BuyoutRightsConfig;
  columns: TableColumn[] = [];

  @ViewChild('stepper') stepper!: AssetSettingsStepperComponent;
  @ViewChild('fileDragDrop') fileDragDrop!: FileDragNDropComponent;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    @Inject(MAT_DIALOG_DATA) private readonly data: AnyType,
    private readonly dialogRef: MatDialogRef<AssetUploadComponent>,
    private readonly canPipe: CanPipe,
    private readonly assetsService: AssetsApiService,
    private readonly tasksApiService: TasksApiService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly fileUploadService: FileUploadService,
    private readonly valueListsService: ValueListsService,
    private readonly assetCommonService: AssetCommonService,
    private readonly projectsApiService: ProjectsApiService,
    private readonly assetUploadService: AssetUploadService,
    private readonly usersTaggingApiService: UsersTaggingApiService,
    private readonly clientNotificationHandlerService: ClientNotificationHandlerService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    private readonly relationService: RelationsApiService,
    private readonly assetSettingsStepperConfigService: AssetSettingsStepperConfigService,
    private readonly assetUrlService: AssetsGenerateUrlService,
    private readonly collectionAPIService: CollectionsApiService,
    private readonly productBrandSelectionService: ProductBrandSelectionService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.assetSettingsStepperConfigService.populateUsers().subscribe(async (users) => {
      let stepperData: StepperModel;
      const tagsAccessibility = !this.canPipe.transform(this.AnyAsset, 'tagging');
      switch (this.brandingKey) {
        case CustomClients.Arval:
          stepperData = await this.assetSettingsStepperConfigService.getArvalAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          break;
        case CustomClients.VKB:
          stepperData = await this.assetSettingsStepperConfigService.getVKBAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          break;
        case CustomClients.SEAT:
          stepperData = await this.assetSettingsStepperConfigService.getSeatAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          break;
        case CustomClients.LufthansaMediacenter:
        case CustomClients.LufthansaMedialounge:
          stepperData = await this.assetSettingsStepperConfigService.getLufthansaAssetSettingsStepperConfig(this.users, { disableTag: tagsAccessibility });
          break;
        case CustomClients.Vorwerk:
          stepperData = await this.assetSettingsStepperConfigService.getVorwerkAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          break;
        case CustomClients.Allianz:
          stepperData = await this.assetSettingsStepperConfigService.getAllianzAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          break;
        default:
          stepperData = await this.assetSettingsStepperConfigService.getStandardAssetSettingsStepperConfig(users, { disableTag: tagsAccessibility });
          this.columns = BuyoutRightsColumnData.TABLE_MAKEOS_COLUMNS;
          this.buyoutRightsConfigs = getBuyoutRightsConfig(this.brandingKey);
          break;
      }

      this.currentStep = stepperData.stepper[0];

      if (this.data) {
        if (this.data['callfrom'] === 'draft' && this.data['assets'] && this.data['assets'].length > 0) {
          this.draftFlag = true;
          this.data['assets'].forEach((asset: AnyType) => {
            this.existingAssetListFiles.push(asset);
            asset['key'] = asset['name'];
            this.assetListFilesDraft.push(asset);
            this.selectedAssetIds.push(asset._id);
          });
        } else {
          this.updateFormOnEdit(stepperData);

          this.stepperData = {
            stepperData: stepperData,
            additionalData: [],
            callFrom: 'assetUpload',
            editable: true,
          };

          this.changeDetectorRef.detectChanges();
          this.isReady = true;

          return;
        }
      }

      this.valueListsService.listUserAgencies(['asset.upload']).subscribe((response) => {
        const fields = stepperData.stepper[0].formFields || [];
        const formArrayField = fields?.find((field) => field.fieldType === FieldType.FormArray && field.formFieldName === 'domains');
        if (formArrayField) updateAgencyOptions(formArrayField.formFieldGroup || [], response);
        else updateAgencyOptions(fields, response);

        this.stepperData = {
          stepperData: stepperData,
          additionalData: [],
          callFrom: 'assetUpload',
          editable: true,
        };
        this.changeDetectorRef.detectChanges();
        this.isReady = true;
      });
    });

    this.fileUploadsSubscription();
  }

  ngAfterViewChecked() {
    if (this.stepper) {
      this.stepper.assetUrlService = this.assetUrlService;
    }
  }

  onReady(): void {
    this.form = this.stepper.stepperFormGroups;
    this.assetUploadBuyoutRights = this.stepper.assetUploadBuyoutRights;

    const ownerField = this.getStepperFormControl('owner');
    if (ownerField) {
      const currentUser = this.getCurrentUser();
      ownerField.setValue(currentUser);
    }
  }

  private updateFormOnEdit(stepperData: StepperModel) {
    const dataAssetObject = this.data.assets.filter((asset: Asset) => asset._id === this.data.mainAssetId);
    this.clients = [this.data.client];
    this.brands = [this.data.brand];

    const fields = stepperData.stepper[0].formFields;
    fields?.forEach((field: AnyType) => {
      switch (field.formFieldName) {
        case 'agency':
          field.options = [this.data.agency];
          field.disable = true;
          break;
        case 'client':
          field.options = [this.data.client];
          field.disable = true;
          break;
        case 'brand':
          field.options = [this.data.brand];
          field.disable = true;
          break;
        case 'status':
          field.value = this.data['publishAsset'] ? Asset.StatusEnum.Approved : Asset.StatusEnum.Uploaded;
          field.disable = this.data['publishAsset'];
          break;
        case 'name':
          field.value = this.data['publishAsset'] ? dataAssetObject[0] && dataAssetObject[0].name : '';
          field.disable = this.data['publishAsset'];
          break;
        default:
      }
    });
  }

  onFieldChange(fieldData: AnyType) {
    switch (fieldData.formFieldName) {
      case 'files':
        this.addFiles(fieldData.event);
        break;
      case 'agency':
        this.onAgencySelected(fieldData.rowIndex, fieldData.event, fieldData.formGroupField);
        break;
      case 'client':
        this.onClientSelected(fieldData.rowIndex, fieldData.event, fieldData.formGroupField, fieldData);
        break;
      case 'brand':
        this.onBrandSelected(fieldData);
        break;
      case 'brandMarke':
        this.onBrandMarkeSelected(fieldData);
        break;
      case 'lufthansaContent':
        this.onLufthansaContentSelected(fieldData);
        break;
      case 'brandModel':
        this.onBrandModelSelected(fieldData);
        break;
      case 'expiryDate':
        this.onExpiryDateSelected(fieldData.event);
        break;
      case 'noExpiry':
        this.onNoExpirySelected(fieldData.event);
        break;
      case 'attachments':
        this.onAttachmentsSelected(fieldData.event);
        break;
      case 'publishStartDate': {
        this.onPublishStartDateSelected(fieldData.event);
        break;
      }
      case 'publishEndDate': {
        this.onPublishEndDateSelected(fieldData.event);
        break;
      }
      case 'plannedDeletionToggle': {
        this.onPlannedDeletionToggle(fieldData.event);
        break;
      }
      default:
        switch (this.brandingKey) {
          case CustomClients.Arval:
            this.assetCommonService.updateArvalFormData(fieldData.formFieldName, fieldData.formGroupField);
            break;
          case CustomClients.Vorwerk: {
            if (fieldData.formFieldName === 'product') {
              this.productBrandSelectionService.updateOnProductSelection(
                fieldData,
                this.stepper.stepperFormGroups.controls[fieldData.formGroupField.stepperGroupName],
              );
            }
            break;
          }
          default:
            break;
        }
    }
  }

  private onAgencySelected(rowIndex: number, event: AnyType, formGroup?: AnyType): void {
    if (!this.data || this.draftFlag) {
      this.clients = event.value.clients;

      const clientField = formGroup.formFields.find((field: AnyType) => field.formFieldName === 'client');
      const brandField = formGroup.formFields.find((field: AnyType) => field.formFieldName === 'brand');

      if (clientField) {
        clientField.value = '';
        clientField.options = this.clients;
        if (clientField.nestedOptions) clientField.nestedOptions[rowIndex] = this.clients;
      }

      if (brandField && !event.source?.singleValue) {
        brandField.value = '';
        brandField.options = [];
      }

      this.taggingOptions.agency = event.value._id;
    }
  }

  private onClientSelected(rowIndex: number, event: AnyType, formGroup?: AnyType, fieldData?: AnyType): void {
    if (!this.data || this.draftFlag) {
      this.brands = event.value.brands;
      const brandField = formGroup.formFields.find((field: AnyType) => field.formFieldName === 'brand');
      if (brandField) {
        brandField.value = '';
        brandField.options = this.brands;
        if (brandField.nestedOptions) brandField.nestedOptions[rowIndex] = this.brands;
      }
      this.taggingOptions.client = event.value._id;
    }

    const tagStepperIndex = fieldData.initialStepper.stepper.findIndex((stepper: AnyType) => stepper.stepperGroupName === AssetMetadata.General.TAGS);
    if (tagStepperIndex > -1) {
      const contentFieldIndex = fieldData.initialStepper.stepper[tagStepperIndex].formFields.findIndex(
        (formField: AnyType) => formField.formFieldName === 'lufthansaContent',
      );
      const productFieldIndex = fieldData.initialStepper.stepper[tagStepperIndex].formFields.findIndex(
        (formField: AnyType) => formField.formFieldName === 'lufthansaProduct',
      );
      if (contentFieldIndex > -1) {
        if (event.value._id === LufthansaGroupID) {
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaContent')?.addValidators(Validators.required);
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[contentFieldIndex].visible = true;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[contentFieldIndex].mandatory = true;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[contentFieldIndex].validators = { required: true };
        } else {
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[contentFieldIndex].visible = false;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].visible = false;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[contentFieldIndex].validators = { required: false };
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaContent')?.patchValue('');
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaContent')?.removeValidators(Validators.required);
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaContent')?.updateValueAndValidity();
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.removeValidators(Validators.required);
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.updateValueAndValidity();
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.patchValue('');
        }
      }
    }
  }

  private onBrandSelected(fieldData: AnyType) {
    this.taggingOptions.brand = fieldData.event.value._id;
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.productBrandSelectionService.updateOnBrandSelection(fieldData, this.stepper.stepperFormGroups.controls[fieldData.formGroupField.stepperGroupName]);
    }
    if (this.brandingKey === CustomClients.SEAT) {
      this.productBrandSelectionService.updateOnBrandSelectionSEAT(
        fieldData,
        this.stepper.stepperFormGroups.controls[fieldData.formGroupField.stepperGroupName],
      );
    }
  }

  private onLufthansaContentSelected(fieldData: AnyType) {
    const event = fieldData.event;
    const tagStepperIndex = fieldData.initialStepper.stepper.findIndex((stepper: AnyType) => stepper.stepperGroupName === AssetMetadata.General.TAGS);
    if (tagStepperIndex > -1) {
      const productFieldIndex = fieldData.initialStepper.stepper[tagStepperIndex].formFields.findIndex(
        (formField: AnyType) => formField.formFieldName === 'lufthansaProduct',
      );
      if (productFieldIndex > -1) {
        if (event.value._id === 'product') {
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.addValidators(Validators.required);
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.updateValueAndValidity();
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].visible = true;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].mandatory = true;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].validators = { required: true };
        } else {
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].visible = false;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].mandatory = false;
          fieldData.initialStepper.stepper[tagStepperIndex].formFields[productFieldIndex].validators = { required: false };
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.removeValidators(Validators.required);
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.updateValueAndValidity();
          this.stepper.stepperFormGroups.controls[AssetMetadata.General.TAGS].get('lufthansaProduct')?.patchValue('');
        }
      }
    }
  }

  private onBrandMarkeSelected(fieldData: AnyType) {
    if (this.brandingKey === CustomClients.SEAT) {
      this.productBrandSelectionService.updateOnBrandMarkeSelectionSEAT(
        fieldData,
        this.stepper.stepperFormGroups.controls[fieldData.formGroupField.stepperGroupName],
      );
    }
  }

  private onBrandModelSelected(fieldData: AnyType) {
    if (this.brandingKey === CustomClients.SEAT) {
      setTimeout(() => {
        this.productBrandSelectionService.updateOnBrandModelSelectionSEAT(
          fieldData,
          this.stepper.stepperFormGroups.controls[fieldData.formGroupField.stepperGroupName],
        );
      }, 0);
    }
  }

  private onExpiryDateSelected(event: AnyType) {
    const noExpiryField = this.getStepperFormControl('noExpiry');
    if (event.value) {
      noExpiryField?.patchValue(false);
    }
  }

  private onNoExpirySelected(event: AnyType) {
    const expiryDateField = this.getStepperFormControl('expiryDate');
    if (expiryDateField && event.checked) {
      expiryDateField?.clearValidators();
      expiryDateField.reset();
    } else {
      expiryDateField?.setValidators(Validators.required);
    }
    expiryDateField?.updateValueAndValidity();
  }

  private onAttachmentsSelected(event: AnyType) {
    // Store the attachments in a component property, cause the sp-file-drag-n-drop
    // does not provide the value when submitting the form (this.form.getRawValue)
    this.attachments = event || [];
    // Clear file input after storing the value to ensure, that the same file could be
    // added again after it might be removed via its file-list component.
    const attachmentsInput = this.elementRef.nativeElement.querySelector('#fileDropRef');
    if (attachmentsInput) attachmentsInput.value = '';
  }

  private onPublishStartDateSelected(event: AnyType): void {
    const publishStartDateField = this.getStepperFormControl('publishStartDate');
    const publishEndDateField = this.getStepperFormControl('publishEndDate');

    if (publishStartDateField && publishEndDateField) {
      checkStartDateAgainstEndDate(publishStartDateField, publishEndDateField, event);
    }
  }

  private onPublishEndDateSelected(event: AnyType): void {
    const publishStartDateField = this.getStepperFormControl('publishStartDate');
    const publishEndDateField = this.getStepperFormControl('publishEndDate');

    if (publishStartDateField && publishEndDateField) {
      checkEndDateAgainstStartDate(publishStartDateField, publishEndDateField, event);
    }
  }

  private onPlannedDeletionToggle(event: AnyType): void {
    const plannedDeletionField = this.getStepperFormControl('plannedDeletion');

    if (plannedDeletionField) {
      this.enableDisablePlannedDeletionField(plannedDeletionField, event);
    }
  }

  private enableDisablePlannedDeletionField(plannedDeletionField: FormControl<AnyType>, event: AnyType): void {
    if (event.checked) {
      plannedDeletionField.enable();
      plannedDeletionField.setValidators(Validators.required);
    } else {
      plannedDeletionField.setValue('');
      plannedDeletionField.disable();
      plannedDeletionField.clearValidators();
    }
    plannedDeletionField.updateValueAndValidity();
  }

  private singleFileNameValidation(): void {
    const nameField = this.getStepperFormControl('name');
    if (!nameField) {
      return;
    }

    if (this.files.length === 1) {
      nameField.addValidators(Validators.required);
      // Asset name should be taken from file name while uploading a single media file (https://app.clickup.com/t/2vxggar)
      nameField.patchValue(this.files[0].name);
      nameField.enable();
    } else {
      nameField.clearValidators();
      nameField.patchValue('');
      nameField.disable();
    }

    nameField.updateValueAndValidity();
  }

  private updateProject(assets: Asset[]) {
    const projectID = this.data._id;
    this.data.assets.push(...assets);
    this.projectsApiService.projectsControllerUpdate(projectID, { assets: this.data.assets }).subscribe({
      error: (err) => this.onError(err),
    });
  }

  private updateSubProject(assets: Asset[]) {
    const subProjectID = this.data._id;
    this.data.assets.push(...assets);
    this.projectsApiService.projectsControllerUpdate(subProjectID, { assets: this.data.assets }).subscribe({
      error: (err) => this.onError(err),
    });
  }

  private updateTask(assets: Asset[]) {
    const assetIds = assets.map((asset) => asset._id);
    this.tasksApiService.tasksControllerAddAssetsToTask(this.data._id, { assets: assetIds }).subscribe({
      error: (err) => this.onError(err),
    });
  }

  cancelUpload() {
    if (!this.form.pristine) {
      this.confirmDialog
        .open({
          title: $localize`Confirm`,
          message: $localize`Are you sure you want to close? All unsaved data will be lost`,
          confirmCaption: $localize`:dialog button:Yes`,
          cancelCaption: $localize`:dialog button:No`,
        })
        .subscribe((data) => {
          if (data) this.dialogRef.close();
        });
    } else {
      this.dialogRef.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (/^esc/i.test(event.key)) {
      this.cancelUpload();
    }
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  /*-------------------- File upload and assets creation funcs ---------------------*/

  async onSubmit(_event: AnyType) {
    this.hasFilesError = false;

    if (this.draftFlag) {
      this.updateAssetOnDraft();
    } else {
      if (!this.files.length) {
        // goto first step
        this.hasFilesError = true;

        this.stepper.nextGoToStep({
          actionLabel: '',
          actionType: '',
          actionTypePosition: '',
          visible: true,
          goToStep: 1,
        });
        return;
      }

      if (this.form.valid) {
        this.dialogRef.disableClose = true;
        if (this.data && this.data['publishAsset']) {
          this.updateAssetData();
        } else {
          this.uploadInProgress = true;
          this.startUpload();
        }
      }
    }
  }

  updateAssetData() {
    this.form.disable();
    const formValues: UpdateAssetDto = this.form.getRawValue();
    const assetID = this.data.mainAssetId;
    const projectId = this.data.parentProjectId;
    this.assetsService.assetsControllerUpdate(assetID, formValues, projectId).subscribe({
      next: (res) => {
        if (res) {
          this.dialogRef.close(res);
          this.clientNotificationHandlerService.successNotification($localize`Asset published to DAM`);
        }
      },
      error: this.clientNotificationHandlerService.errorNotification,
    });
  }

  async updateAssetOnDraft() {
    const rawFormValues: AnyType = this.form.getRawValue();
    const formValues = await this.buildCommonFormValues(rawFormValues);

    if ('name' in formValues) {
      delete formValues.name;
    }

    const formData: UpdateAssetManyDto = {
      ids: this.selectedAssetIds,
      updateData: formValues,
    };
    this.assetsService.assetsControllerUpdateMany(formData).subscribe({
      next: (res) => {
        if (res) {
          this.dialogRef.close(res);
          this.assets = this.assetListFilesDraft;
          this.assets.forEach((asset) => {
            this.createAssetRelations({}, asset._id);
          });
          this._handleAddToCollections();
          this.clientNotificationHandlerService.successNotification($localize`Assets updated successfully`);
        }
      },
      error: this.clientNotificationHandlerService.errorNotification,
    });
  }

  private async buildCommonFormValues(rawFormValues: AnyType, assetUploadBuyoutRights?: AnyType[]): Promise<AnyType> {
    const customFormValues: AnyType = this.getCustomFormValues(rawFormValues);

    // Normalize tags
    if (rawFormValues.tags?.tags?.length) {
      rawFormValues.tags.tags = await this.assetCommonService.modifiedTags(rawFormValues.tags.tags);
    }

    if (rawFormValues.tags?.tags === null || rawFormValues.tags?.tags.length === 0) {
      delete rawFormValues.tags.tags;
    }

    // Handle usage rights
    if (rawFormValues.usageRightsGeneralInformation) {
      if (this.brandingKey === CustomClients.Standard) {
        rawFormValues['makeBuyoutRights'] = processStandardBuyoutRights(assetUploadBuyoutRights || [], this.assetCommonService);
      } else {
        processNonStandardBuyoutRights(rawFormValues);
      }
      delete rawFormValues.usageRightsGeneralInformation;
    }

    // Attach uploaded files to usage rights
    if (rawFormValues.usageRight) {
      rawFormValues.usageRight.attachments = this.attachments;
    }

    // Normalize status field
    rawFormValues.generalInformation.status = rawFormValues.generalInformation.status.id
      ? rawFormValues.generalInformation.status.id
      : rawFormValues.generalInformation.status;

    return constructFormValues(rawFormValues, customFormValues);
  }

  private getCustomFormValues(rawFormValues: AnyType): AnyType {
    switch (this.brandingKey) {
      case CustomClients.Arval:
        return handleArvalBranding(rawFormValues, this.brandingKey, this.assetCommonService);
      case CustomClients.Vorwerk:
        return handleVorwerkBranding(rawFormValues, this.assetCommonService);
      case CustomClients.VKB:
        return handleVKBBranding(rawFormValues, this.assetCommonService);
      case CustomClients.SEAT:
        return handleSeatBranding(rawFormValues, this.assetCommonService);
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        return handleLufthansaBranding(rawFormValues, this.assetCommonService);
      default:
        return handleStandardBranding(rawFormValues, this.assetCommonService);
    }
  }

  async startUpload() {
    // disable form when upload starts (https://app.clickup.com/t/325w3cq)
    this.form.disable();

    try {
      for await (const fileObj of this.files) {
        if (!fileObj.uploaded) {
          await this.processFile(fileObj);
        }
      }
    } catch (err) {
      this.onError(err);
    }
  }

  /**
   * Process uploaded file
   * @param fileObj
   */
  async processFile(fileObj: FileObject): Promise<void> {
    const incompleteUpload = await this.assetUploadService.checkForIncompleteUploads(fileObj, this.files);
    if (typeof incompleteUpload === 'boolean') {
      const rawFormValues: AnyType = this.form.getRawValue();
      const assetUploadBuyoutRights: AnyType[] = this.assetUploadBuyoutRights.getRawValue();

      const formValues = await this.buildCommonFormValues(rawFormValues, assetUploadBuyoutRights);

      if (this.data) {
        formValues.clusterType = 'Briefing document';
      }
      // create asset
      const asset = await this.assetUploadService.createAsset(fileObj, formValues);
      // notification for tagged user in WYSIWYG editor
      if (asset && this.taggedUser && this.taggedUser.length) {
        this.notifyTaggedUsers(asset);
      }

      const key = createAssetFileKey(asset);
      this.assets.push(asset);
      this.assetsMap.set(key, asset);

      const fileIndex = this.files.findIndex((fileobj) => fileobj.name === fileObj.name);
      const file = this.files[fileIndex];

      this.files[fileIndex] = { ...file, key: key };

      await this.assetUploadService.uploadFile({ key: key, file: fileObj.file });
    } else {
      await this.assetUploadService.uploadFile(incompleteUpload);
    }
  }

  /**
   * Notify tagged user from the WYSIWYG editor
   * @param asset
   */
  notifyTaggedUsers(asset: AnyType): void {
    const notifyOptions: NotifyOptions = {
      userInfo: this.taggedUser,
      callFromContext: '',
      contextPath: 'asset',
      contextId: asset._id,
      contextName: asset.name,
      editorDescription: this.descriptionText,
    };
    this.usersTaggingApiService.usersTaggingControllerNotifyTaggedUsers(notifyOptions).subscribe();
  }

  fileUploadsSubscription(): void {
    this.progressSubscription = this.fileUploadService.progress.subscribe((data) => {
      this.progress = data;
    });
    this.uploadStartSubscription = this.fileUploadService.fileUploadStartSubject.subscribe({
      next: (fileData) => {
        this.assetUploadService.updateFileDataOnStart(fileData, this.files);
      },
      error: this.onError,
    });
    this.uploadCompleteSubscription = this.fileUploadService.uploadedFileSubject.subscribe({
      next: (uploadedData) => {
        this.updateFileDataOnComplete(uploadedData);
        this.createAssetRelations(uploadedData);
      },
      error: this.onError,
    });
  }

  /**
   * Create the asset relation between the uploaded asset and the selected assets
   */
  private createAssetRelations(uploadedFileData: AnyType, assetId?: string) {
    let uploadedAsset: AnyType = {};
    if (!this.draftFlag) {
      uploadedAsset = this.assetsMap.get(uploadedFileData.key);
      if (!uploadedAsset) {
        return;
      }
    } else {
      uploadedAsset._id = assetId;
    }

    const stepperRawValue = this.stepper.stepperFormGroups.getRawValue();
    const relatedAssets = stepperRawValue?.defineAssetRelation?.assetRelation.assets as AssetRelationsUIModel[];
    if (!relatedAssets?.length) {
      return;
    }

    for (const relatedAsset of relatedAssets) {
      for (const relationType of relatedAsset.relationTypes) {
        const request: CreateAssetRelationDto = {
          fromEntityId: relatedAsset.assetId,
          fromEntity: 'Asset',
          toEntityId: uploadedAsset._id,
          toEntity: 'Asset',
          relationshipType: relationType._id as unknown as string,
        };

        this.relationService.relationsControllerCreateRelation(request).subscribe({
          error: this.clientNotificationHandlerService.errorNotification,
        });
      }
    }
  }

  updateFileDataOnComplete(uploadedFileData: AnyType): void {
    const fileIndex = this.files.findIndex((fileObj) => fileObj.key === uploadedFileData.key);
    const file = this.files[fileIndex];
    this.files[fileIndex] = { ...file, uploaded: true, uploading: false };
    if (this.files[fileIndex + 1]) {
      this.currentUploadFileName = this.files[fileIndex + 1].name;
      if (fileIndex + 1 < this.files.length && this.progress === 100) {
        this.progress = 0;
      }
    }
    if (fileIndex + 1 === this.files.length && this.progress === 100) {
      this.uploadInProgress = false;
      this.dialogRef.disableClose = false;
      if (this.data && this.data.type === 'Project') {
        this.updateProject(this.assets);
      }
      if (this.data && this.data.type === 'Sub-Project') {
        this.updateSubProject(this.assets);
      }
      if (this.data && (this.data.taskType === 'Task' || this.data.taskType === 'Sub-Task')) {
        this.updateTask(this.assets);
      }
      this._handleAddToCollections();
      this.dialogRef.close(this.assets);
    }
  }

  private _handleAddToCollections() {
    const rawFormValues: AnyType = this.form.getRawValue();

    // if addToExistingCollectionMetadata available add assets to existing collection
    if (rawFormValues.addToExistingCollectionMetadata && rawFormValues.addToExistingCollectionMetadata.add2ExistingCollection) {
      const existingCollectionId = rawFormValues.addToExistingCollectionMetadata.add2ExistingCollection.id;
      this.collectionAPIService.collectionsControllerFindOne(existingCollectionId).subscribe((resp: Collection) => {
        const assetIDs = resp.assets?.map((asset: AnyType) => asset._id) || [];
        const newAssetIds = this.assets.map((asset) => asset._id);
        const newCollectionAssets = assetIDs.length ? assetIDs.concat(newAssetIds) : newAssetIds;
        this.collectionAPIService
          .collectionsControllerUpdate(existingCollectionId, {
            assets: [...new Set(newCollectionAssets)],
          })
          .subscribe();
      });
    }

    // if createNewCollectionMetadata available create new collection
    if (rawFormValues.createNewCollectionMetadata) {
      this._createNewCollection(rawFormValues.createNewCollectionMetadata);
    }
  }

  private async _createNewCollection(collectionMetadata: { name: string; description: string; keywords: string[]; cover?: AnyType; owners?: string[] }) {
    const payloadObj: CreateCollectionDto = {
      name: collectionMetadata.name,
      description: collectionMetadata.description,
      keywords: await this.assetCommonService.modifiedTags(collectionMetadata.keywords),
      assets: this.assets.map((asset) => asset._id),
    };
    // if cover available create coverAssetID property
    if (collectionMetadata.cover) {
      // some large asset have imageUrl large data:image value, so deleting it before adding to pay load
      // this breaks the collection creation api with error 'request entity too large'
      delete collectionMetadata.cover.imageUrl;
      const coverAsset = this.assets.find((asset) => asset.name === collectionMetadata.cover._id);
      payloadObj.coverAssetID = {
        ...collectionMetadata.cover,
        _id: coverAsset?._id,
      };
    }
    // if owners available create ownerIDs property
    if (collectionMetadata.owners) {
      payloadObj.ownerIDs = collectionMetadata.owners;
    }

    this.collectionAPIService.collectionsControllerCreate(payloadObj).subscribe();
  }

  onError(err: unknown) {
    this.uploadInProgress = false;
    this.dialogRef.disableClose = false;
    this.clientNotificationHandlerService.errorNotification(err);
  }

  ngOnDestroy(): void {
    this.progressSubscription && this.progressSubscription.unsubscribe();
    this.uploadStartSubscription && this.uploadStartSubscription.unsubscribe();
    this.uploadCompleteSubscription && this.uploadCompleteSubscription.unsubscribe();
  }

  async addFiles(fileList: FileObject[]): Promise<FileObject[]> {
    if (fileList && fileList?.length) {
      const files = fileList.concat(this.files);

      this.files = files;
      this.hasFilesError = false;
      this.createAssetListFiles();
    }

    this.singleFileNameValidation();

    this.fileDragDrop.clear();
    await this.checkExistingAsset();
    return this.files;
  }

  async removeFile(file: FileObject, index: number): Promise<FileObject[]> {
    this.files.splice(index, 1);
    this.createAssetListFiles();
    this.singleFileNameValidation();
    await this.checkExistingAsset();
    return this.files;
  }

  onNameFieldChange(event: AnyType) {
    if (this.files && this.files[0].name !== event) {
      this.assetExistingFlag = false;
      this.files[0].isExists = false;
    } else {
      this.assetExistingFlag = true;
      this.files[0].isExists = true;
    }
  }

  getUploadedAssetsFileNames(): string[] {
    return this.files.map((file) => file.name);
  }

  async checkExistingAsset() {
    this.assetDuplicateFlag = false;
    this.assetExistingFlag = false;
    const assetNames = this.getUploadedAssetsFileNames() || [];
    this.files.forEach((file) => {
      if (assetNames.filter((name: string) => name === file.name).length > 1) {
        file.isDuplicate = true;
        this.assetDuplicateFlag = true;
      } else {
        file.isDuplicate = false;
      }
    });

    this.assetsService.assetsControllerExistingAssets(JSON.stringify(assetNames)).subscribe({
      next: (res) => {
        if (res && res.length > 0) {
          res.forEach((assetName: string) => {
            const fileIndex = this.files.findIndex((file: FileObject) => file.name === assetName);
            this.files[fileIndex].isExists = true;
            this.assetExistingFlag = true;
          });
        }
      },
    });
  }

  /**
   * Creates modified list of files that match the data shape for the asset-list-component
   */
  createAssetListFiles(fileList: FileObject[] = this.files): Promise<AssetListFileObject[]> {
    return new Promise((resolve, reject) => {
      if (!fileList.length) {
        this.assetListFiles = [];
        resolve(this.assetListFiles);
        return;
      }

      const previewSupportedMimeTypes = ['image/jpeg', 'image/webp', 'image/gif', 'image/png', 'image/avif', 'image/svg+xml'];
      const fileListCopy = [...(fileList as AnyType[])];
      const assetList: AssetListFileObject[] = [];

      /* Assamble file item data */
      const filesListPromises: Promise<AnyType>[] = fileListCopy.map((fileItem) => {
        return new Promise((resolve2, reject2) => {
          this.assetUploadService
            .checkAssetType(fileItem)
            .then((assetInfo) => {
              fileItem.assetType = assetInfo.assetTypeToReturn;
              assetList.push(fileItem);
              resolve2(fileItem);
            })
            .catch(() => {
              reject2();
            });
        });
      });

      /* Create file item list and assign preview images */
      Promise.all(filesListPromises)
        .then(() => {
          this.assetListFiles = assetList;

          const filesWithPreview = fileListCopy.filter((fileItem) => {
            return fileItem.assetType === AssetTypeEnum.Image && previewSupportedMimeTypes.includes(fileItem.file.type);
          });

          filesWithPreview.forEach(async (fileItem, i) => {
            if (fileItem.previewUrl) return;

            fileItem.isProcessing = true;

            try {
              const previewUrl = await this.fileUploadService.readFile(fileItem.file);
              if (previewUrl) fileItem.previewUrl = previewUrl;
            } finally {
              fileItem.isProcessing = false;

              if (i === filesWithPreview.length - 1) resolve(assetList);
            }
          });
        })
        .catch(() => {
          reject();
        });
    });
  }

  getStepperFormControl(fieldName: string): FormControl | undefined {
    let formControl: FormControl | undefined;
    Object.keys(this.form.controls).forEach((key) => {
      if (formControl) {
        return;
      }
      const formGroupField = this.form.controls[key] as UntypedFormGroup;
      if (formGroupField?.controls[fieldName]) {
        formControl = formGroupField?.controls[fieldName] as FormControl;
      }
    });
    return formControl;
  }

  onBeforeStepChange(): void {
    if (this.currentStep.stepperId === '1' && this.files.length < 1 && !this.draftFlag) {
      this.hasFilesError = true;
    }
  }

  onStepChange(currentStep: StepperGroupModel): void {
    if (this.currentStep.stepperId === '1' && this.files.length < 1 && !this.draftFlag) {
      this.hasFilesError = true;
      this.stepper.showSelectedStepper(this.stepperData.stepperData.stepper[0], 0, true);
      return;
    }

    this.currentStep = currentStep;
  }

  trackByFn(index: number, item: AbstractControl) {
    return item.value.buyoutRightsTitle?._id;
  }

  private getCurrentUser(): UserInfo | undefined {
    return this.assetCommonService.currentUser?.userData
      ? {
          id: this.assetCommonService.currentUser.userData._id,
          name: this.assetCommonService.currentUser.userData.firstName + ' ' + this.assetCommonService.currentUser.userData.lastName,
          email: this.assetCommonService.currentUser.userData.email,
        }
      : undefined;
  }
}
